<script>
import {
  CdrText,
  CdrGrid,
  CdrLink,
  IconCaretLeft,
  IconCaretRight,
  CdrBreadcrumb,
  CdrRating,
  CdrLandingLead,
} from '@rei/cedar';
import BylineAndPublishDate from './components/BylineAndPublishDate.vue';
import BioCard from './components/BioCard.vue';
import SocialShare from './components/SocialShare.vue';
import SubmitRating from '../../components/SubmitRating.vue';
import RelatedLinks from '../../components/RelatedLinks.vue';
import RenderDynamicContent from '../../components/RenderDynamicContent.vue';
import SeriesTile from './components/SeriesTile.vue';
import TableOfContents from './components/TableOfContents.vue';
import ProductBanner from './components/ProductBanner.vue';

import initAnalytics, { setSiteId, setPageName } from '../../utils/analytics';

import AdobeTarget from '../../utils/adobeTarget';

// adding support for contentStack live preview
import { fetchData } from '../../utils/api';
import ReturnToPurchasePath from '../../components/ReturnToPurchasePath/ReturnToPurchasePath.vue';

export default {
  name: 'DetailsPage',
  components: {
    ReturnToPurchasePath,
    CdrText,
    CdrGrid,
    CdrLink,
    IconCaretLeft,
    IconCaretRight,
    CdrBreadcrumb,
    CdrRating,
    BylineAndPublishDate,
    BioCard,
    SocialShare,
    SubmitRating,
    RelatedLinks,
    RenderDynamicContent,
    SeriesTile,
    TableOfContents,
    ProductBanner,
    CdrLandingLead,
  },
  inheritAttrs: false,
  props: {
    articleName: {
      type: String,
      default: '',
    },
    article: {
      type: Object,
      default: null,
    },
    authors: {
      type: Array,
      default: null,
    },
    contributors: {
      type: Array,
      default: null,
    },
    seriesNav: {
      type: Object,
      default: null,
    },
    previous: {
      type: Object,
      default: null,
    },
    next: {
      type: Object,
      default: null,
    },
    breadCrumbs: {
      type: Array,
      default: null,
    },
    bazaarVoiceApi: {
      type: Object,
      default: null,
    },
    relatedLinks: {
      type: Array,
      default: null,
    },
    referrerProduct: {
      type: Object,
      default: null,
    },
    tocEnabled: {
      type: Boolean,
      required: false,
    },
    showStickyBanner: {
      type: Boolean,
      default: false,
    },
    productSearchBanner: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      baseClassName: 'ea-details-page',
      content: {
        article: this.article,
        contributors: this.contributors,
        authors: this.authors,
      },
      productRecommendationsComponentNames: ['product-recommendations', 'product-recommendations-gendered'],
      choiceAwardWinnerText: ' & Editors’ Choice Award Winner',
      targetExperience: 'Control',
    };
  },
  computed: {
    isGearGuides() {
      return this.article.contentType === 'Gear Guides';
    },
    isSeriesPage() {
      return this.seriesNav !== null;
    },
    isVariantExperience() {
      return this.targetExperience === 'Variant';
    },
    hasPrevious() {
      if (this.previous === null) return false;
      return this.previous.uri;
    },
    hasNext() {
      if (this.next === null) return false;
      return this.next.uri;
    },
    lastInSeries() {
      return this.next.articleId === null;
    },
    articleId() {
      return this.content?.article?.id;
    },
    firstReviewIndex() {
      return this.getContentBlocks.findIndex((i) => this.productRecommendationsComponentNames.includes(i.component));
    },
    hasAuthors() {
      return this.content?.authors && this.content?.authors?.length > 0;
    },
    hasContributors() {
      return this.content?.contributors && this.content?.contributors?.length > 0;
    },
    hasLeadImage() {
      return this.content?.article && this.content?.article?.heroImage;
    },
    hasRating() {
      return this.content?.article?.averageRating && this.content?.article?.reviewCount;
    },
    hasRelatedLinks() {
      // related links not editable in contentstack
      return this.relatedLinks && this.relatedLinks.length > 0;
    },
    hasReviews() {
      return this.firstReviewIndex !== -1;
    },
    getContentBlocks() {
      // returns an array of objects representing article content broken into components
      return this.content?.article?.contentBlocks || [];
    },
    getContentBlocksBeforeReviews() {
      return [...this.getContentBlocks].splice(0, this.firstReviewIndex);
    },
    getContentBlocksAfterReviews() {
      // returns an array of objects representing article content broken into components

      const {
        productRecommendationsComponentNames,
        choiceAwardWinnerText,
        getContentBlocks,
        firstReviewIndex,
      } = this;

      return [...getContentBlocks].splice(firstReviewIndex).map((block) => {
        if (productRecommendationsComponentNames.includes(block.component)) {
          const badgeText = block.data.badge ? choiceAwardWinnerText : '';
          const tagline = block.data.tagline ? `${block.data.tagline}${badgeText}` : '';

          return {
            ...block,
            data: {
              ...block.data,
              tagline,
            },
          };
        }
        return block;
      });
    },
    renderPage() {
      return this.content?.article
        && this.breadCrumbs;
    },
  },
  async mounted() {
    setSiteId('expert advice');
    setPageName('article');
    initAnalytics('#ea_details-page');

    // Register for Live Preview Update Events
    window.addEventListener('OnLivePreviewUpdate', (event) => {
      const hash = event?.detail?.hash;
      if (hash) {
        fetchData(`/learn/article/preview/${this.articleName}?hash=${hash}`, this.updateData);
      }
    });

    this.targetExperience = await AdobeTarget.getExperience('EA_Sticky Banner');
  },
  methods: {
    updateData(data) {
      this.content = data;
      this.$forceUpdate();
    },
  },
};
</script>

<template>
  <div
    v-if="renderPage"
    id="ea_details-page"
    :class="`${baseClassName}`"
  >
    <cdr-grid :class="`${baseClassName}__grid`">
      <section :class="`${baseClassName}__lead`">
        <cdr-breadcrumb
          :truncation-enabled="false"
          :items="breadCrumbs"
          :class="`${baseClassName}__breadcrumbs`"
        />
        <header>
          <cdr-text
            tag="h1"
            :class="`${baseClassName}__heading`"
          >
            {{ content?.article?.title }}
          </cdr-text>
          <cdr-text
            v-if="content?.article?.subtitle"
            :class="`${baseClassName}__subtitle`"
          >
            {{ content?.article?.subtitle }}
          </cdr-text>
        </header>
        <div :class="`${baseClassName}__credits`">
          <byline-and-publish-date
            :date-published="content?.article?.datePublished"
            :date-updated="content?.article?.dateModified"
            :authors="content.authors"
          />
        </div>
        <div :class="`${baseClassName}__extras`">
          <cdr-rating
            v-if="hasRating"
            :rating="article.averageRating"
            :count="article.reviewCount"
            data-test="article-rating"
            size="small"
          />
          <social-share
            :article-title="content?.article?.title"
            :article-uri="content?.article?.uri"
            :class="`${baseClassName}__social-share`"
          />
        </div>
        <cdr-text
          v-if="isSeriesPage"
        >
          <em>
            This article is part of our series:
            <cdr-link
              :href="`${seriesNav.uri}`"
              data-test="series-title"
            >
              {{ seriesNav.title }}
            </cdr-link>
          </em>
        </cdr-text>

        <cdr-landing-lead
          v-if="hasLeadImage"
          class="ea-hero-image-container"
          :img-src="content?.article?.heroImage"
          :img-alt="content?.article?.heroImageAltText"
        />
      </section>
      <div
        v-if="hasPrevious"
        :class="`${baseClassName}__left-side ${baseClassName}__hidden-md-down`"
        data-test="previous-paddle"
      >
        <div>
          <series-tile
            :uri="previous.uri"
            :title="previous.title"
          >
            <icon-caret-left size="small" />
            <span>Previous Article</span>
          </series-tile>
        </div>
      </div>
      <article
        id="ea_article-body"
        :class="`${baseClassName}__article`"
      >
        <template v-if="hasReviews">
          <render-dynamic-content
            v-for="(block, index) in getContentBlocksBeforeReviews"
            :key="`block-${index}`"
            v-bind="block"
          />

          <table-of-contents
            v-if="tocEnabled && isGearGuides"
            data-test="table-of-contents"
            :article-type="article.articleType"
            :content-blocks="getContentBlocks"
          />

          <render-dynamic-content
            v-for="(block, index) in getContentBlocksAfterReviews"
            :key="`block-2-${index}`"
            v-bind="block"
          />
        </template>
        <template v-else>
          <render-dynamic-content
            v-for="(block, index) in getContentBlocks"
            :key="`block-${index}`"
            v-bind="block"
          />
        </template>
      </article>
      <div
        v-if="hasNext"
        :class="`${baseClassName}__right-side ${baseClassName}__hidden-md-down`"
        data-test="next-paddle"
      >
        <div>
          <series-tile
            :uri="next.uri"
            :title="next.title"
          >
            <span v-if="lastInSeries">Learn More</span>
            <span v-if="!lastInSeries">
              <span>Next Article</span>
              <icon-caret-right size="small" />
            </span>
          </series-tile>
        </div>
      </div>
      <div :class="`${baseClassName}__links-section`">
        <div
          v-if="isSeriesPage"
          :class="`${baseClassName}__series-links`"
        >
          <div
            v-if="hasPrevious"
            :class="`${baseClassName}__series-link`"
          >
            <series-tile
              :uri="previous.uri"
              :title="previous.title"
              :description="previous.description"
            >
              <span>Previous Article</span>
            </series-tile>
          </div>
          <div
            v-if="hasPrevious && hasNext"
            :class="`${baseClassName}__series-link-spacer`"
          />
          <div
            v-if="hasNext"
            :class="`${baseClassName}__series-link`"
          >
            <series-tile
              :uri="next.uri"
              :title="next.title"
              :description="next.description"
            >
              <span v-if="lastInSeries">Learn More</span>
              <span v-if="!lastInSeries">Next Article</span>
            </series-tile>
          </div>
        </div>
        <related-links
          v-if="hasRelatedLinks"
          :related-links="relatedLinks"
        />
        <section>
          <div id="rating-submit">
            <hr>
            <submit-rating
              :bazaar-voice-api="bazaarVoiceApi"
              :article-id="articleId"
            />
          </div>
        </section>
        <section
          v-if="hasAuthors"
          data-test="authors"
        >
          <div>
            <hr>
            <cdr-text
              id="article-authors"
              tag="h2"
              :class="`${baseClassName}__contributor-heading`"
            >
              Authors
            </cdr-text>
            <cdr-grid :class="`${baseClassName}__contributor-grid`">
              <bio-card
                v-for="(author, index) in content.authors"
                :key="`author-${index}`"
                :bio="author"
              />
            </cdr-grid>
          </div>
        </section>
        <section
          v-if="hasContributors"
          data-test="contributors"
        >
          <div>
            <hr>
            <cdr-text
              id="article-experts"
              tag="h2"
              :class="`${baseClassName}__contributor-heading`"
            >
              Contributing Experts
            </cdr-text>
            <cdr-grid :class="`${baseClassName}__contributor-grid`">
              <bio-card
                v-for="(contributor, index) in content.contributors"
                :key="`contributor-${index}`"
                :bio="contributor"
              />
            </cdr-grid>
          </div>
        </section>
        <section>
          <div>
            <hr>
            <cdr-link href="#container">
              Back to top
            </cdr-link>
          </div>
        </section>
      </div>
    </cdr-grid>

    <ReturnToPurchasePath
      v-if="referrerProduct"
      v-bind="referrerProduct"
    />
  </div>

  <ProductBanner
    v-if="showStickyBanner && productSearchBanner && isVariantExperience && !referrerProduct"
    :data="productSearchBanner"
    data-test="product-search-banner"
    data-ui="related-articles"
  />
</template>

<style lang="scss">
  @import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
  @import '../../../style/common/editor-styles.scss';

  $baseClassName: 'ea-details-page';

  h1, h2, h3, h4, p {
    margin-block-end: unset;
    margin-block-start: unset;
  }

.#{$baseClassName} {

  @include cdr-container;
  margin-bottom: $cdr-space-two-x;

  // grid configuration
  @include cdr-xs-mq-only {
    .#{$baseClassName}__grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  @include cdr-md-mq-up {
    .#{$baseClassName}__grid {
      grid-template-columns: 1fr 4fr 1fr;
      grid-template-rows: auto;
      grid-template-areas:
        " . lead . "
        "left-side article right-side"
        " . links . ";
    }
    .#{$baseClassName}__lead {
      grid-area: lead;
      & > * + *  {
        margin: $cdr-space-one-x 0 0 0;
      }
    }
    .#{$baseClassName}__left-side {
      grid-area: left-side;
    }
    .#{$baseClassName}__article {
      grid-area: article;
    }
    .#{$baseClassName}__right-side {
      grid-area: right-side;
    }
    .#{$baseClassName}__links-section {
      grid-area: links;
    }
  }

  hr {
    border: none;
    border-top: 1px solid $cdr-color-background-secondary;
    margin: $cdr-space-one-and-a-half-x 0;
  }
  .#{$baseClassName}__breadcrumbs {
    margin-top: $cdr-space-one-and-a-half-x;
  }
  .#{$baseClassName}__heading {
    @include cdr-text-heading-serif-800;
    @include cdr-sm-mq-up {
      @include cdr-text-heading-serif-strong-900;
    }
  }
  .#{$baseClassName}__subtitle {
    @include cdr-text-subheading-sans-500;
    margin-top: $cdr-space-three-quarter-x;
  }
  .#{$baseClassName}__extras {
    display: flex;
    @include cdr-xs-mq-only {
      flex-direction: column;
    }
    > .ea-details-page__social-share {
      flex: 1 1;
      display: flex;
      justify-content: flex-end;
      @include cdr-xs-mq-only {
        padding-top: $cdr-space-half-x;
        height: 3em;
        justify-content: flex-start;
      }
    }
  }
  .#{$baseClassName}__credits {
    flex: 1 1;
    display: flex;

    @include cdr-text-utility-sans-300;
  }
  .#{$baseClassName}__hidden-md-down {
    @include cdr-md-mq-down {
      display: none;
    }
  }
  .#{$baseClassName}__right-side {
    text-align: right;
  }
  .#{$baseClassName}__series-links {
    display: flex;
    margin: 0;
    padding: 0;
    @include cdr-xs-mq-only {
      flex-direction: column;
    }
  }
  .#{$baseClassName}__series-link {
    flex: 1 1 0;
    margin-bottom: $cdr-space-one-x;
  }
  .#{$baseClassName}__series-link-spacer {
    flex: 0 0 auto;
    width: $cdr-space-two-x;
  }
  .#{$baseClassName}__contributor-heading {
    @include cdr-text-heading-serif-600;
    margin-bottom: $cdr-space-two-x;
  }
  .#{$baseClassName}__contributor-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }
}

.ea_img-responsive img {
  display: block;
  max-width: 100%;
  height: auto;
}

// Smooth scroll for back to top button
html {
  scroll-behavior: smooth;
}
@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
}
</style>
